import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowRight, CheckCircle } from 'lucide-react';

const HERO_IMG = 'https://images.unsplash.com/photo-1758691463620-188ca7c1a04f?w=1200&q=85&auto=format&fit=crop';

export default function HakkimizdaPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #15263A 0%, #1E3348 80px, #F8F6F3 80px, #F8F6F3 100%)' }}>
      <Helmet>
        <title>Hakkımızda — DoktorCebinde</title>
        <meta name="description" content="DoktorCebinde, zihinsel sağlık ve bilişsel performansı daha anlaşılır hale getirmek amacıyla geliştirilmiş bir dijital sağlık platformudur." />
      </Helmet>
      <Header />

      <article style={{ maxWidth: 1100, margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Hero Image */}
        <div style={{ borderRadius: 24, overflow: 'hidden', marginBottom: 56, boxShadow: '0 12px 40px rgba(0,0,0,0.08)' }} data-testid="hakkimizda-hero-img">
          <img
            src={HERO_IMG}
            alt="Modern klinik ortamında danışmanlık"
            style={{ width: '100%', height: 420, objectFit: 'cover', display: 'block' }}
            loading="eager"
          />
        </div>

        {/* Title */}
        <h1 style={{ fontSize: 36, fontWeight: 700, color: '#1A2B3C', textAlign: 'center', marginBottom: 32, letterSpacing: '-0.02em' }} data-testid="hakkimizda-title">
          Hakkımızda
        </h1>

        {/* Intro */}
        <div style={{ maxWidth: 760, margin: '0 auto 64px' }} data-testid="hakkimizda-intro">
          <p style={{ fontSize: 17, lineHeight: 1.8, color: '#435160', marginBottom: 16 }}>
            DoktorCebinde, zihinsel sağlık ve bilişsel performansı daha anlaşılır hale getirmek amacıyla geliştirilmiş bir dijital sağlık platformudur.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: '#435160', margin: 0 }}>
            Günlük yaşamda stres, uyku problemleri, dikkat dağınıklığı ve unutkanlık gibi durumlar çoğu zaman fark edilmeden ilerler. Bu platform, kullanıcıların bu değişimleri erken fark etmelerine yardımcı olmak için tasarlanmıştır.
          </p>
        </div>

        {/* Section: Amacımız */}
        <section style={{ maxWidth: 760, margin: '0 auto 64px' }} data-testid="hakkimizda-amac">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>Amacımız</h2>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: '#435160', marginBottom: 16 }}>
            Amacımız, bilimsel temellere dayanan değerlendirme yaklaşımlarını herkes için erişilebilir ve anlaşılır hale getirmektir.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: '#435160', marginBottom: 12 }}>Bu doğrultuda:</p>
          <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D7A86E', marginTop: 8, flexShrink: 0 }} />
              karmaşık verileri sadeleştiriyoruz
            </li>
            <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D7A86E', marginTop: 8, flexShrink: 0 }} />
              kullanıcıya farkındalık kazandırıyoruz
            </li>
            <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D7A86E', marginTop: 8, flexShrink: 0 }} />
              günlük hayata uygulanabilir öneriler sunuyoruz
            </li>
          </ul>
        </section>

        {/* Section: Nasıl Çalışır? */}
        <section style={{ maxWidth: 760, margin: '0 auto 64px' }} data-testid="hakkimizda-nasil">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>Nasıl Çalışır?</h2>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: '#435160', marginBottom: 16 }}>
            DoktorCebinde, kullanıcıların tamamladığı kısa testleri ve girdikleri verileri birlikte değerlendirir.
          </p>
          <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px' }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', marginBottom: 12 }}>Sistem:</p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6F90B1', marginTop: 8, flexShrink: 0 }} />
                tek bir sonuca değil, genel tabloya bakar
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6F90B1', marginTop: 8, flexShrink: 0 }} />
                farklı alanlardaki değişimleri birlikte yorumlar
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6F90B1', marginTop: 8, flexShrink: 0 }} />
                kullanıcıya sade ve açıklayıcı geri bildirim sunar
              </li>
            </ul>
          </div>
        </section>

        {/* Section: Bilimsel Yaklaşımımız */}
        <section style={{ maxWidth: 760, margin: '0 auto 64px' }} data-testid="hakkimizda-bilimsel">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>Bilimsel Yaklaşımımız</h2>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: '#435160', marginBottom: 16 }}>
            Platformumuz, nörobilim, klinik psikoloji ve davranış bilimleri alanlarında yapılan araştırmalardan yararlanılarak geliştirilmiştir.
          </p>

          <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px', marginBottom: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', marginBottom: 12 }}>Kullanılan değerlendirme mantıkları:</p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A9E7A', marginTop: 8, flexShrink: 0 }} />
                bilimsel ölçeklerin prensiplerinden esinlenir
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A9E7A', marginTop: 8, flexShrink: 0 }} />
                klinik çalışmalarda kullanılan yaklaşımlara dayanır
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A9E7A', marginTop: 8, flexShrink: 0 }} />
                kullanıcı dostu olacak şekilde sadeleştirilmiştir
              </li>
            </ul>
          </div>

          <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px' }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', marginBottom: 12 }}>DoktorCebinde:</p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D88A61', marginTop: 8, flexShrink: 0 }} />
                rastgele sonuç üretmez
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D88A61', marginTop: 8, flexShrink: 0 }} />
                veri odaklı çalışır
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#D88A61', marginTop: 8, flexShrink: 0 }} />
                bilimsel temele dayanır
              </li>
            </ul>
          </div>
        </section>

        {/* Section: Neyi Amaçlamıyoruz */}
        <section style={{ maxWidth: 760, margin: '0 auto 64px' }} data-testid="hakkimizda-amaclama">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1A2B3C', marginBottom: 16 }}>Neyi Amaçlamıyoruz</h2>

          <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px', marginBottom: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', marginBottom: 12 }}>Bu platform:</p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#94A3B8', marginTop: 8, flexShrink: 0 }} />
                tıbbi tanı koymaz
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#94A3B8', marginTop: 8, flexShrink: 0 }} />
                klinik değerlendirme yerine geçmez
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#94A3B8', marginTop: 8, flexShrink: 0 }} />
                bir doktor muayenesinin alternatifi değildir
              </li>
            </ul>
          </div>

          <div style={{ background: '#fff', border: '1px solid #ECE7E1', borderRadius: 14, padding: '24px 28px' }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#1A2B3C', marginBottom: 12 }}>Amaç:</p>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A9E7A', marginTop: 8, flexShrink: 0 }} />
                kullanıcıya farkındalık kazandırmak
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A9E7A', marginTop: 8, flexShrink: 0 }} />
                erken değişimleri fark etmeye yardımcı olmak
              </li>
              <li className="flex items-start gap-3" style={{ fontSize: 15, color: '#435160', lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#5A9E7A', marginTop: 8, flexShrink: 0 }} />
                sağlıklı alışkanlıklar oluşturmayı desteklemek
              </li>
            </ul>
          </div>
        </section>

        {/* Section: Neden DoktorCebinde? */}
        <section style={{ maxWidth: 760, margin: '0 auto 64px' }} data-testid="hakkimizda-neden">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1A2B3C', marginBottom: 20 }}>Neden DoktorCebinde?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'Bilimsel temellere dayanır',
              'Kişiye özel değerlendirme sunar',
              'Sade ve anlaşılırdır',
              'Günlük hayata uygulanabilir öneriler verir',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3" style={{
                background: '#fff', border: '1px solid #ECE7E1', borderRadius: 12,
                padding: '16px 20px',
              }}>
                <CheckCircle size={18} style={{ color: '#5A9E7A', flexShrink: 0 }} />
                <span style={{ fontSize: 15, fontWeight: 500, color: '#1A2B3C' }}>{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }} data-testid="hakkimizda-cta">
          <p style={{ fontSize: 18, color: '#435160', marginBottom: 20, lineHeight: 1.6 }}>
            Kendi analiz sürecinizi başlatmak ister misiniz?
          </p>
          <button
            onClick={() => navigate('/register')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: '#D7A86E', color: '#162233',
              fontSize: 15, fontWeight: 600, padding: '14px 28px',
              borderRadius: 4, border: 'none', cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            data-testid="hakkimizda-cta-btn"
          >
            Analizime Başla <ArrowRight size={17} />
          </button>
        </section>

      </article>

      <Footer />
    </div>
  );
}
